/**
 * PainelGoogleAds — visão das campanhas do Google Ads dentro da página Analytics.
 *
 * Fonte: /api/ads/*, alimentado 1x/dia pelo script que roda dentro do painel do
 * Google Ads. Independe do GA4 — por isso vive num componente próprio e continua
 * funcionando com a integração do Analytics desligada.
 *
 * Duas decisões de leitura que valem para todos os números daqui:
 *
 * 1) RANKING DE PALAVRA-CHAVE PEDE JANELA LONGA. A conta faz ~3 conversões por
 *    semana; num recorte de poucos dias, um único clique reordena tudo. O padrão
 *    é 90 dias e o período aparece escrito ao lado do título.
 *
 * 2) GASTO × FATURAMENTO NÃO É ROAS. O faturamento do dia inclui venda orgânica,
 *    direta e recorrente — nada liga um pedido à campanha (o gclid não é
 *    capturado no checkout). Por isso o retorno atribuído pelo Google aparece
 *    separado, com esse nome, e o cruzamento é rotulado como participação.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Table, Badge, Spinner, Alert, Button, Modal } from 'react-bootstrap';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
    fetchAdsResumo, fetchAdsCampanhas, fetchAdsPalavras,
    fetchAdsTermos, fetchAdsGastoFaturamento,
    fetchAdsOportunidades, fetchAdsPlanoAcao,
} from '../../services/api';

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const num = (v) => Number(v || 0).toLocaleString('pt-BR');
/** null = não há base para calcular. Mostrar "0%" aqui seria inventar informação. */
const pct = (v) => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR')}%`);
const diaCurto = (d) => (d ? d.slice(8, 10) + '/' + d.slice(5, 7) : '');

/** Datas em horário LOCAL: toISOString() vira o dia seguinte depois das 21h em UTC-3. */
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hoje = new Date();
const diasAtras = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoLocal(d); };

/**
 * Retorno = valor convertido ÷ investido, pelo que o GOOGLE atribui à campanha.
 * Sem investimento não existe retorno (null, não zero) — e null cai para o fim na
 * ordenação, para campanha parada não parecer a de pior retorno da lista.
 */
const comRetorno = (campanhas = []) => campanhas.map((c) => ({
    ...c,
    roas: c.custo > 0 ? Math.round((Number(c.valorConversao || 0) / c.custo) * 100) / 100 : null,
}));

const PERIODOS = [
    { label: '7 dias', dias: 7 },
    { label: '30 dias', dias: 30 },
    { label: '90 dias', dias: 90 },
];

/**
 * Ordenação por coluna, clicando no cabeçalho (1º clique desc, 2º asc).
 *
 * Regra que vale para TODAS as tabelas da página: valor NULO vai sempre para o fim, nas duas
 * direções. CTR, CPC e custo/conversão são null quando não há base para calcular
 * (campanha sem impressão, sem clique ou sem conversão) — ordenar "menor CPC"
 * traria essas linhas primeiro, sugerindo que são as mais baratas, quando na
 * verdade não têm número nenhum.
 */
function useOrdenacao(colunaInicial, direcaoInicial = 'desc') {
    const [ordem, setOrdem] = useState({ coluna: colunaInicial, direcao: direcaoInicial });

    const alternar = (coluna) => setOrdem((o) => (
        o.coluna === coluna
            ? { coluna, direcao: o.direcao === 'desc' ? 'asc' : 'desc' }
            : { coluna, direcao: 'desc' }
    ));

    const ordenar = (lista = []) => [...lista].sort((a, b) => {
        const x = a[ordem.coluna];
        const y = b[ordem.coluna];
        if (x == null && y == null) return 0;
        if (x == null) return 1;
        if (y == null) return -1;
        const cmp = typeof x === 'string' && typeof y === 'string'
            ? x.localeCompare(y, 'pt-BR')
            : Number(x) - Number(y);
        return ordem.direcao === 'asc' ? cmp : -cmp;
    });

    return { ordem, alternar, ordenar };
}

/**
 * Cabeçalho clicável com a seta do estado atual.
 * `dica` explica a métrica no tooltip nativo — várias colunas aqui (CTR, CPC,
 * retorno) são jargão de anúncio que ninguém é obrigado a saber de cor.
 */
function Th({ campo, ordem, alternar, children, alinhar = 'end', dica }) {
    const ativo = ordem.coluna === campo;
    return (
        <th
            onClick={() => alternar(campo)}
            className={`text-${alinhar} user-select-none`}
            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
            title={dica ? `${dica}

(clique para ordenar)` : 'Clique para ordenar'}
        >
            {children}{' '}
            <span style={{ opacity: ativo ? 1 : 0.25, fontSize: '0.7rem' }}>
                {ativo ? (ordem.direcao === 'desc' ? '▼' : '▲') : '↕'}
            </span>
        </th>
    );
}

/**
 * Um dos três rankings de palavra-chave. A LISTA continua sendo o recorte do
 * bloco (quem mais converte, quem só gasta, quem consome mais verba) — a
 * ordenação aqui reorganiza esse recorte, não troca o critério de seleção.
 */
function BlocoPalavras({ titulo, lista, periodo, vazio, ordemInicial }) {
    const { ordem, alternar, ordenar } = useOrdenacao(ordemInicial);
    return (
        <Card className="h-100 shadow-sm">
            <Card.Header className="py-2">
                <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{titulo}</div>
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                    {periodo.inicio} a {periodo.fim}
                </div>
            </Card.Header>
            <Card.Body className="p-0">
                {!lista?.length ? (
                    <div className="text-muted text-center py-4" style={{ fontSize: '0.82rem' }}>{vazio}</div>
                ) : (
                    <Table size="sm" className="mb-0" hover style={{ fontSize: '0.8rem' }}>
                        <thead className="table-light">
                            <tr>
                                <Th campo="texto" ordem={ordem} alternar={alternar} dica="Palavra-chave que você comprou no Google Ads. Embaixo, a campanha dela." alinhar="start">Palavra</Th>
                                <Th campo="cliques" ordem={ordem} alternar={alternar} dica="Cliques que essa palavra trouxe no período.">Cliq.</Th>
                                <Th campo="custo" ordem={ordem} alternar={alternar} dica="Quanto essa palavra consumiu de verba.">Investido</Th>
                                <Th campo="conversoes" ordem={ordem} alternar={alternar} dica="Conversões atribuídas a essa palavra pelo Google.">Conv.</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordenar(lista).map((k) => (
                                <tr key={k.texto}>
                                    <td>
                                        <div className="fw-semibold">{k.texto}</div>
                                        <div className="text-muted" style={{ fontSize: '0.68rem' }}>{k.campanha}</div>
                                    </td>
                                    <td className="text-end align-middle">{num(k.cliques)}</td>
                                    <td className="text-end align-middle fw-bold" style={{ whiteSpace: 'nowrap' }}>{brl(k.custo)}</td>
                                    <td className="text-end align-middle">
                                        {k.conversoes > 0 ? <Badge bg="success">{k.conversoes}</Badge> : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );
}


/**
 * Variação contra o período anterior de mesmo tamanho.
 *
 * `inverter` para custo: gastar mais não é "bom" nem "ruim" por si só, mas subir
 * custo pinta de vermelho e cair de verde, que é a leitura de quem controla verba.
 * Quando não há base de comparação (período anterior sem dado), não mostra nada —
 * "+100%" ali seria invenção.
 */
function Variacao({ valor, inverter = false }) {
    if (valor == null) return null;
    const subiu = valor > 0;
    const neutro = valor === 0;
    const bom = inverter ? !subiu : subiu;
    const cor = neutro ? 'text-muted' : bom ? 'text-success' : 'text-danger';
    const seta = neutro ? '=' : subiu ? '▲' : '▼';
    return (
        <div className={`${cor} fw-semibold`} style={{ fontSize: '0.72rem' }} title="Comparado ao período anterior de mesmo tamanho">
            {seta} {Math.abs(valor).toLocaleString('pt-BR')}% vs. período anterior
        </div>
    );
}


const corDoTipo = (tipo) => (tipo === 'economia' ? 'danger' : tipo === 'oportunidade' ? 'success' : 'warning');
const rotuloDoTipo = (tipo) => (tipo === 'oportunidade' ? 'Retorno em jogo' : 'Verba em jogo');

/**
 * Junta as ações por tipo (negativar termo, rever palavra-chave, aumentar
 * orçamento...). Cada grupo vira um card; o detalhe abre em modal. Antes eram 25
 * linhas soltas numa tabela, que ninguém lê até o fim.
 * Grupos ordenados pelo total em jogo — o card mais caro primeiro.
 */
function agruparAcoes(acoes = []) {
    const mapa = new Map();
    for (const a of acoes) {
        const grupo = mapa.get(a.acao) || { acao: a.acao, tipo: a.tipo, itens: [], total: 0 };
        grupo.itens.push(a);
        grupo.total += Number(a.verbaEmJogo || 0);
        mapa.set(a.acao, grupo);
    }
    return [...mapa.values()]
        .map((g) => ({ ...g, total: Math.round(g.total * 100) / 100 }))
        .sort((a, b) => b.total - a.total);
}

export default function PainelGoogleAds() {
    // 90 dias por padrão: é a janela em que o ranking de palavra-chave desta conta
    // para de ser ruído (ver cabeçalho).
    const [periodoDias, setPeriodoDias] = useState(90);
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [grupoAberto, setGrupoAberto] = useState(null);   // card do plano clicado
    const ordemCampanhas = useOrdenacao('custo');
    const ordemTermos = useOrdenacao('custo');

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        const params = { dataInicio: diasAtras(periodoDias), dataFim: isoLocal(hoje) };
        try {
            // A rota /alertas continua existindo no backend (serve para automação
            // futura, tipo notificação diária), mas a TELA não a consome mais: os
            // cards do plano de ação cobrem o mesmo conteúdo em formato acionável.
            const [resumo, campanhas, palavras, termos, gastoFat, oportunidades, plano] = await Promise.all([
                fetchAdsResumo(params), fetchAdsCampanhas(params), fetchAdsPalavras(params),
                fetchAdsTermos({ ...params, limite: 20 }),
                fetchAdsGastoFaturamento(params), fetchAdsOportunidades(params), fetchAdsPlanoAcao(params),
            ]);
            setDados({ resumo, campanhas, palavras, termos, gastoFat, oportunidades, plano });
        } catch (e) {
            console.error('[Ads] Falha ao carregar painel:', e);
            setErro('Não consegui carregar os dados do Google Ads.');
        } finally {
            setCarregando(false);
        }
    }, [periodoDias]);

    useEffect(() => { carregar(); }, [carregar]);

    if (carregando && !dados) {
        return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
    }
    if (erro) {
        return (
            <Alert variant="danger" className="mt-3">
                {erro} <Button size="sm" variant="outline-danger" className="ms-2" onClick={carregar}>Tentar de novo</Button>
            </Alert>
        );
    }

    const kpis = dados?.resumo?.kpis || {};
    const periodo = dados?.resumo?.periodo || {};
    const variacao = dados?.resumo?.comparativo?.variacao || {};
    const periodoAnterior = dados?.resumo?.comparativo?.anterior?.periodo;
    const semDado = !kpis.impressoes && !kpis.custo;

    return (
        <div className="pt-2">
            {/* Período */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <span className="text-muted small">Período:</span>
                {PERIODOS.map((p) => (
                    <Button key={p.dias} size="sm"
                        variant={periodoDias === p.dias ? 'primary' : 'outline-secondary'}
                        onClick={() => setPeriodoDias(p.dias)}>
                        {p.label}
                    </Button>
                ))}
                <span className="text-muted small ms-2">
                    {periodo.inicio} a {periodo.fim} · {periodo.dias || 0} dia(s) com dado
                    {periodoAnterior && ` · comparando com ${periodoAnterior.inicio} a ${periodoAnterior.fim}`}
                </span>
                <Button size="sm" variant="outline-secondary" className="ms-auto" onClick={carregar}>
                    ↻ Atualizar
                </Button>
            </div>

            {semDado && (
                <Alert variant="warning">
                    Nenhum dado do Google Ads neste período. Os números chegam quando o script
                    roda dentro do painel do Ads — se acabou de instalar, execute-o uma vez.
                </Alert>
            )}

            {/* KPIs */}
            <Row className="g-3 mb-3">
                {[
                    { titulo: 'Investido', valor: brl(kpis.custo), variacao: variacao.custo, inverter: true },
                    { titulo: 'Cliques', valor: num(kpis.cliques), extra: `${num(kpis.impressoes)} impressões`, variacao: variacao.cliques },
                    { titulo: 'CTR', valor: pct(kpis.ctr), extra: `CPC ${kpis.cpc == null ? '—' : brl(kpis.cpc)}` },
                    { titulo: 'Conversões', valor: num(kpis.conversoes), extra: kpis.custoPorConversao == null ? 'sem conversão' : `${brl(kpis.custoPorConversao)} cada`, variacao: variacao.conversoes },
                    { titulo: 'Retorno atribuído pelo Google', valor: brl(kpis.valorConversao), extra: kpis.roasGoogle == null ? '—' : `${kpis.roasGoogle}x o investido`, variacao: variacao.valorConversao },
                ].map((k) => (
                    <Col key={k.titulo} md={6} lg>
                        <Card className="h-100 shadow-sm">
                            <Card.Body className="py-3">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{k.titulo}</div>
                                <div className="fw-bold" style={{ fontSize: '1.4rem' }}>{k.valor}</div>
                                {k.extra && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{k.extra}</div>}
                                <Variacao valor={k.variacao} inverter={k.inverter} />
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Plano de ação — agrupado por tipo de ação; o detalhe abre em modal
                para a tela não virar uma lista de 25 linhas soltas. */}
            {dados?.plano?.acoes?.length > 0 && (
                <Card className="mb-3 shadow-sm border-primary">
                    <Card.Header className="bg-primary bg-opacity-10">
                        <div className="fw-bold">🎯 Plano de ação</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Clique num card para ver os itens. Os de economia somam{' '}
                            <strong>{brl(dados.plano.totais.verbaEmEconomia)}</strong> no período — é o quanto está indo
                            para termos e palavras que não converteram, não uma economia garantida (cortar um clique
                            pode empurrá-lo para outra palavra).
                        </div>
                        {dados.plano.desperdicio?.custo > 0 && (
                            <div className="mt-2">
                                <Badge bg="danger" className="me-2">
                                    {dados.plano.desperdicio.percentualDoInvestido ?? '—'}% do investido
                                </Badge>
                                <span style={{ fontSize: '0.8rem' }}>
                                    {dados.plano.desperdicio.palavras} palavra(s) consumiram{' '}
                                    <strong>{brl(dados.plano.desperdicio.custo)}</strong> sem nenhuma conversão
                                    {dados.plano.totais.investido
                                        ? ` de ${brl(dados.plano.totais.investido)} investidos.`
                                        : '.'}
                                </span>
                            </div>
                        )}
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-3">
                            {agruparAcoes(dados.plano.acoes).map((g) => (
                                <Col key={g.acao} md={6} xl={4}>
                                    <Card
                                        className={`h-100 shadow-sm border-${corDoTipo(g.tipo)}`}
                                        role="button"
                                        onClick={() => setGrupoAberto(g)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setGrupoAberto(g); }}
                                        tabIndex={0}
                                        title="Clique para ver os itens"
                                    >
                                        <Card.Body className="py-3">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <Badge bg={corDoTipo(g.tipo)} className="mb-2">{g.acao}</Badge>
                                                    <div className="fw-bold" style={{ fontSize: '1.3rem' }}>
                                                        {g.itens.length} {g.itens.length === 1 ? 'item' : 'itens'}
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {rotuloDoTipo(g.tipo)}: <strong>{brl(g.total)}</strong>
                                                    </div>
                                                </div>
                                                <span className="text-muted" style={{ fontSize: '1.2rem' }}>›</span>
                                            </div>
                                            <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                                                {g.itens.slice(0, 2).map((i) => i.alvo).join(' · ')}
                                                {g.itens.length > 2 && ` · +${g.itens.length - 2}`}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Detalhe do card clicado */}
            <Modal show={!!grupoAberto} onHide={() => setGrupoAberto(null)} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.05rem' }}>
                        <Badge bg={corDoTipo(grupoAberto?.tipo)} className="me-2">{grupoAberto?.acao}</Badge>
                        {grupoAberto?.itens?.length} {grupoAberto?.itens?.length === 1 ? 'item' : 'itens'} ·{' '}
                        {brl(grupoAberto?.total)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <Table hover size="sm" className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Alvo</th>
                                <th>Por quê</th>
                                <th className="text-end" style={{ whiteSpace: 'nowrap' }}>Verba em jogo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(grupoAberto?.itens || []).map((a, i) => (
                                <tr key={i}>
                                    <td style={{ fontSize: '0.85rem' }}>
                                        <div className="fw-semibold">{a.alvo}</div>
                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{a.onde}</div>
                                    </td>
                                    <td className="text-muted" style={{ fontSize: '0.78rem' }}>{a.porque}</td>
                                    <td className="text-end fw-bold" style={{ whiteSpace: 'nowrap' }}>{brl(a.verbaEmJogo)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                        {grupoAberto?.tipo === 'oportunidade'
                            ? 'Verba em jogo aqui é o retorno que pode crescer, não gasto a cortar.'
                            : 'Verba em jogo é o que foi consumido no período — ordem de grandeza para priorizar.'}
                    </span>
                    <Button variant="secondary" size="sm" onClick={() => setGrupoAberto(null)}>Fechar</Button>
                </Modal.Footer>
            </Modal>

            {/* Termos: o que negativar e o que comprar */}
            <Row className="g-3 mb-3">
                <Col lg={7}>
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="py-2">
                            <div className="fw-bold" style={{ fontSize: '0.92rem' }}>🚫 Candidatos a palavra negativa</div>
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                Termos que a pessoa digitou, clicou, custaram dinheiro e nunca converteram.
                                {dados?.oportunidades?.totais && (
                                    <> Somam <strong>{brl(dados.oportunidades.totais.verbaEmTermosSemConversao)}</strong> de{' '}
                                    {num(dados.oportunidades.totais.termos)} termos no período.</>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0" style={{ maxHeight: 320, overflowY: 'auto' }}>
                            {!dados?.oportunidades?.negativar?.length ? (
                                <div className="text-muted text-center py-4" style={{ fontSize: '0.82rem' }}>
                                    Nenhum termo com 2+ cliques sem conversão no período.
                                </div>
                            ) : (
                                <Table size="sm" hover className="mb-0" style={{ fontSize: '0.82rem' }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th title="O que a pessoa digitou no Google">Termo</th>
                                            <th title="Campanha que exibiu o anúncio">Campanha</th>
                                            <th className="text-end" title="Cliques sem nenhuma conversão">Cliq.</th>
                                            <th className="text-end" title="Verba consumida por esse termo">Custo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dados.oportunidades.negativar.map((t, i) => (
                                            <tr key={`${t.termo}-${i}`}>
                                                <td className="fw-semibold">{t.termo}</td>
                                                <td className="text-muted" style={{ fontSize: '0.72rem' }}>{t.campanha}</td>
                                                <td className="text-end">{num(t.cliques)}</td>
                                                <td className="text-end fw-bold">{brl(t.custo)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={5}>
                    <Card className="h-100 shadow-sm">
                        <Card.Header className="py-2">
                            <div className="fw-bold" style={{ fontSize: '0.92rem' }}>✨ Palavras que faltam comprar</div>
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                Termos que CONVERTERAM e ainda não estão entre as palavras compradas — hoje chegam
                                por correspondência ampla, sem controle de lance.
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0" style={{ maxHeight: 320, overflowY: 'auto' }}>
                            {!dados?.oportunidades?.novasPalavras?.length ? (
                                <div className="text-muted text-center py-4" style={{ fontSize: '0.82rem' }}>
                                    Nenhum termo converteu fora das palavras já compradas.
                                </div>
                            ) : (
                                <Table size="sm" hover className="mb-0" style={{ fontSize: '0.82rem' }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Termo</th>
                                            <th className="text-end">Conv.</th>
                                            <th className="text-end">Custo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dados.oportunidades.novasPalavras.map((t, i) => (
                                            <tr key={`${t.termo}-${i}`}>
                                                <td>
                                                    <div className="fw-semibold">{t.termo}</div>
                                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>{t.campanha}</div>
                                                </td>
                                                <td className="text-end"><Badge bg="success">{t.conversoes}</Badge></td>
                                                <td className="text-end">{brl(t.custo)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Gasto x faturamento */}
            <Card className="mb-3 shadow-sm">
                <Card.Header>
                    <div className="fw-bold">Gasto no Ads × faturamento do e-commerce</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        Contexto, não ROAS: o faturamento do dia inclui venda que não veio de anúncio.
                        No período, o investimento equivale a <strong>{pct(dados?.gastoFat?.totais?.participacaoAdsNoFaturamento)}</strong> do
                        faturamento ({brl(dados?.gastoFat?.totais?.custo)} de {brl(dados?.gastoFat?.totais?.faturamentoEcommerce)}).
                        O Google atribui {brl(dados?.gastoFat?.totais?.valorConversaoGoogle)} desse total aos anúncios.
                    </div>
                </Card.Header>
                <Card.Body style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dados?.gastoFat?.dias || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="data" tickFormatter={diaCurto} fontSize={11} />
                            <YAxis yAxisId="e" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <YAxis yAxisId="a" orientation="right" fontSize={11} />
                            <Tooltip formatter={(v, nome) => [brl(v), nome]} labelFormatter={(l) => `Dia ${diaCurto(l)}`} />
                            <Legend />
                            <Line yAxisId="e" type="monotone" dataKey="faturamentoEcommerce" name="Faturamento e-commerce" stroke="#2563eb" dot={false} strokeWidth={2} />
                            <Line yAxisId="a" type="monotone" dataKey="custo" name="Gasto no Ads" stroke="#dc2626" dot={false} strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </Card.Body>
            </Card>

            {/* Palavras-chave — o pedido principal do dono */}
            <Row className="g-3 mb-3">
                {[
                    { titulo: '✅ Mais convertem', lista: dados?.palavras?.maisConvertem, ordemInicial: 'conversoes', vazio: 'Nenhuma palavra-chave converteu no período.' },
                    { titulo: '💸 Gastam sem converter', lista: dados?.palavras?.gastamSemConverter, ordemInicial: 'custo', vazio: 'Nenhuma palavra gastou sem converter.' },
                    { titulo: '🔝 Maior gasto', lista: dados?.palavras?.maiorGasto, ordemInicial: 'custo', vazio: 'Sem gasto por palavra-chave no período.' },
                ].map((bloco) => (
                    <Col key={bloco.titulo} lg={4}>
                        <BlocoPalavras {...bloco} periodo={periodo} />
                    </Col>
                ))}
            </Row>

            {/* Campanhas */}
            <Card className="mb-3 shadow-sm">
                <Card.Header className="fw-bold">Desempenho por campanha</Card.Header>
                <Card.Body className="p-0">
                    <Table responsive hover size="sm" className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <Th campo="nome" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Nome da campanha no Google Ads. Abaixo dele: o tipo e o aviso quando o orçamento está limitando as entregas." alinhar="start">Campanha</Th>
                                <Th campo="impressoes" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Quantas vezes o anúncio apareceu para alguém.">Impressões</Th>
                                <Th campo="cliques" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Quantas vezes clicaram no anúncio e foram para o site.">Cliques</Th>
                                <Th campo="ctr" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Taxa de cliques: cliques dividido por impressões. Mostra se o anúncio atrai quem o vê.">CTR</Th>
                                <Th campo="cpc" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Custo por clique: quanto você pagou, em média, por cada clique.">CPC</Th>
                                <Th campo="custo" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Total investido nessa campanha no período selecionado.">Investido</Th>
                                <Th campo="conversoes" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Conversões que o Google registrou. Pode vir quebrado (1,5) por causa do modelo de atribuição dele.">Conv.</Th>
                                <Th campo="valorConversao" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Receita que o Google atribui a essas conversões — número dele, não o do seu e-commerce.">Valor convertido</Th>
                                <Th campo="roas" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Retorno: valor convertido dividido pelo investido. Acima de 1x, trouxe mais do que custou.">Retorno</Th>
                                <Th campo="custoPorConversao" ordem={ordemCampanhas.ordem} alternar={ordemCampanhas.alternar} dica="Quanto custou, em média, cada conversão.">Custo/conv.</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordemCampanhas.ordenar(comRetorno(dados?.campanhas?.campanhas)).map((c) => (
                                <tr key={c.id}>
                                    <td>
                                        <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{c.nome}</div>
                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                            {c.tipo}
                                            {c.limitadaPorOrcamento && <Badge bg="danger" className="ms-1">limitada pelo orçamento</Badge>}
                                        </div>
                                    </td>
                                    <td className="text-end">{num(c.impressoes)}</td>
                                    <td className="text-end">{num(c.cliques)}</td>
                                    <td className="text-end">{pct(c.ctr)}</td>
                                    <td className="text-end">{c.cpc == null ? '—' : brl(c.cpc)}</td>
                                    <td className="text-end fw-bold">{brl(c.custo)}</td>
                                    <td className="text-end">{num(c.conversoes)}</td>
                                    <td className="text-end fw-bold text-success">
                                        {c.valorConversao > 0 ? brl(c.valorConversao) : '—'}
                                    </td>
                                    <td className="text-end">
                                        {c.roas == null ? '—' : (
                                            <Badge bg={c.roas >= 1 ? 'success' : 'danger'}>
                                                {c.roas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="text-end">{c.custoPorConversao == null ? '—' : brl(c.custoPorConversao)}</td>
                                </tr>
                            ))}
                            {!dados?.campanhas?.campanhas?.length && (
                                <tr><td colSpan={10} className="text-center text-muted py-4">Sem campanhas no período.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Termos de pesquisa */}
            <Card className="shadow-sm">
                <Card.Header>
                    <div className="fw-bold">O que o cliente digitou</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        Termos de pesquisa que acionaram os anúncios — os 20 que mais consumiram verba.
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive hover size="sm" className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <Th campo="termo" ordem={ordemTermos.ordem} alternar={ordemTermos.alternar} dica="O que a pessoa realmente digitou no Google antes de ver seu anúncio." alinhar="start">Termo</Th>
                                <Th campo="campanha" ordem={ordemTermos.ordem} alternar={ordemTermos.alternar} dica="Campanha que exibiu o anúncio para esse termo." alinhar="start">Campanha</Th>
                                <Th campo="cliques" ordem={ordemTermos.ordem} alternar={ordemTermos.alternar} dica="Cliques que esse termo gerou.">Cliques</Th>
                                <Th campo="custo" ordem={ordemTermos.ordem} alternar={ordemTermos.alternar} dica="Quanto esse termo consumiu de verba.">Investido</Th>
                                <Th campo="conversoes" ordem={ordemTermos.ordem} alternar={ordemTermos.alternar} dica="Conversões atribuídas a esse termo.">Conv.</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordemTermos.ordenar(dados?.termos?.termos).map((t, i) => (
                                <tr key={`${t.termo}-${i}`}>
                                    <td style={{ fontSize: '0.85rem' }}>{t.termo}</td>
                                    <td className="text-muted" style={{ fontSize: '0.78rem' }}>{t.campanha}</td>
                                    <td className="text-end">{num(t.cliques)}</td>
                                    <td className="text-end">{brl(t.custo)}</td>
                                    <td className="text-end">{t.conversoes > 0 ? <Badge bg="success">{t.conversoes}</Badge> : '—'}</td>
                                </tr>
                            ))}
                            {!dados?.termos?.termos?.length && (
                                <tr><td colSpan={5} className="text-center text-muted py-4">Sem termos de pesquisa no período.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
}
